import { deleteDoc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import {
  FIRESTORE_V2_STORAGE_MARKER_FIELD,
  FIRESTORE_V2_STORAGE_MARKER_VALUE,
} from "../constants";

const sanitizeFirestoreValue = (value) => {
  if (value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeFirestoreValue(item))
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === "object" && !(value instanceof Date)) {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, nestedValue]) => [key, sanitizeFirestoreValue(nestedValue)])
        .filter(([, nestedValue]) => nestedValue !== undefined)
    );
  }

  return value;
};

export class BaseFirestoreRepository {
  constructor(db) {
    this.db = db;
  }

  async upsert(docRef, data) {
    const sanitizedData = sanitizeFirestoreValue(data);
    await setDoc(docRef, sanitizedData, { merge: true });
    return sanitizedData;
  }

  async get(docRef) {
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? snapshot.data() : null;
  }

  async listByField(collectionRef, fieldName, value) {
    const snapshot = await getDocs(query(collectionRef, where(fieldName, "==", value)));
    return snapshot.docs
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }))
      .filter(
        (document) =>
          document[FIRESTORE_V2_STORAGE_MARKER_FIELD] ===
          FIRESTORE_V2_STORAGE_MARKER_VALUE
      );
  }

  addStorageMarker(data) {
    return {
      ...data,
      [FIRESTORE_V2_STORAGE_MARKER_FIELD]: FIRESTORE_V2_STORAGE_MARKER_VALUE,
    };
  }

  isManagedV2Document(document) {
    return (
      document?.[FIRESTORE_V2_STORAGE_MARKER_FIELD] ===
      FIRESTORE_V2_STORAGE_MARKER_VALUE
    );
  }

  async delete(docRef) {
    await deleteDoc(docRef);
  }
}
